import { refreshTokenApi } from "@/api/services/admin/auth";
import { getCustomSentryExceptionError, handleApiErrorWithCode } from "@/utils/error";
import { logout } from "@/utils/logout";
import { verifyToken } from "@/utils/token";
import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import { createGlobalApi } from "@/modules/irohana-components/utils/api";

if (!process.env.REACT_APP_API_HOST) {
  throw new Error("please provide REACT_APP_API_HOST in env");
}

export const baseURL = `${process.env.REACT_APP_API_HOST}/admin`;

export const globalBaseURL = `${process.env.REACT_APP_API_HOST}/global`;

export const globalApi = axios.create({
  baseURL: globalBaseURL,
});

export const clientApi = axios.create({
  baseURL,
});

// If successful (do nothing)
export const onSuccess = (response: AxiosResponse) => {
  Sentry.endSession();
  return response;
};

type RetryableConfig = AxiosRequestConfig & { _isRetry?: boolean; _skipGlobalError?: boolean };

let isRefreshing = false;
// Each waiting request registers a callback that retries *its own* original
// config once the token is refreshed (or rejects if refresh failed).
let refreshQueue: ((token: string | null) => void)[] = [];

// Retry through the *same* axios instance that made the original request so
// the retried call goes back through its request/response interceptors
// (Sentry session, error handling) instead of bypassing them.
const retryRequestWithToken = (instance: AxiosInstance, config: AxiosRequestConfig, token: string) =>
  instance({
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    },
  });

//  in case of error — created per instance so retries use the right one
export const createOnErrorInterceptor = (instance: AxiosInstance) => async (error: AxiosError) => {
  if (error.response) {
    const originalConfig = error.config as RetryableConfig | undefined;
    // The refresh-token request itself is made through this same instrumented
    // instance. If it 401s (invalid/expired refresh token), it must NOT be
    // queued for retry — that would deadlock the `await refreshTokenApi(...)`
    // call below forever, since a refresh is already in progress and no new
    // one gets kicked off, so the queue never flushes.
    const isRefreshTokenRequest = originalConfig?.url?.includes("/refresh-token");

    // Guard against infinite loops: a request that already got retried once and
    // still 401s falls through to normal error handling instead of re-queuing.
    if (error.response.status === 401 && originalConfig && !originalConfig._isRetry && !isRefreshTokenRequest) {
      originalConfig._isRetry = true;

      // Queue this request so it is retried with the fresh token once refresh
      // completes. The first 401 kicks off the refresh below; every other
      // parallel 401 simply waits in the queue with its own original config.
      const retryPromise = new Promise((resolve, reject) => {
        refreshQueue.push((token) => {
          if (!token) {
            reject(error.response?.data);
            return;
          }
          resolve(retryRequestWithToken(instance, originalConfig, token));
        });
      });

      if (!isRefreshing) {
        isRefreshing = true;

        void (async () => {
          try {
            const refreshToken = Cookies.get("admin-refresh-token");
            const accessToken = Cookies.get("admin-token");
            if (!refreshToken || !accessToken) {
              throw new Error("Missing refresh credentials");
            }

            // A request that raced ahead of setAxiosAuth (no Authorization
            // header) just needs the current cookie token — no refresh needed.
            if (originalConfig.headers?.Authorization) {
              await refreshTokenApi({ refreshToken, accessToken });
            }

            const newToken = Cookies.get("admin-token") ?? null;
            const queue = refreshQueue;
            refreshQueue = [];
            queue.forEach((retry) => retry(newToken));
          } catch (refreshError) {
            logout();
            const queue = refreshQueue;
            refreshQueue = [];
            queue.forEach((retry) => retry(null));
          } finally {
            isRefreshing = false;
          }
        })();
      }

      return retryPromise;
    }
  }
  if (!(error.config as RetryableConfig)?._skipGlobalError) {
    handleApiErrorWithCode(error);
  }

  if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
    Sentry.captureException(getCustomSentryExceptionError(error));
  }
  Sentry.endSession();

  return Promise.reject(error.response?.data);
};

clientApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

globalApi.interceptors.request.use(
  (config) => {
    Sentry.startSession();
    return config;
  },
  (error) => {
    if (error.response?.status && error.response.status >= 500 && error.response.status !== 503) {
      Sentry.captureException(getCustomSentryExceptionError(error));
    }
    Sentry.endSession();
    return Promise.reject(error);
  },
);

clientApi.interceptors.response.use(onSuccess, createOnErrorInterceptor(clientApi));
globalApi.interceptors.response.use(onSuccess, createOnErrorInterceptor(globalApi));

createGlobalApi({
  apiHost: globalBaseURL,
  onError: createOnErrorInterceptor(globalApi),
});

export const setAxiosAuth = () => {
  const token = Cookies.get("admin-token");
  clientApi.defaults.headers.common.Authorization = `Bearer ${token}`;
  globalApi.defaults.headers.common.Authorization = `Bearer ${token}`;
};

export const removeAxiosAuth = () => {
  clientApi.defaults.headers.common.Authorization = "";
  globalApi.defaults.headers.common.Authorization = "";
};

export const isAuth = () => {
  if (typeof window !== "undefined") {
    const token = Cookies.get("admin-token");
    const isAuthenticated = !!token && verifyToken(token);
    return isAuthenticated;
  }
  return false;
};

// Re-exported for backward compatibility: `logout` now lives in `@/utils/logout`
// to avoid a circular import between this module and `@/utils/error`.
export { logout };
